<template>
  <modal :name="nombreModal" :adaptive="true" height="auto" width="560px">
    <div class="baja">
      <div class="modal-header baja-cabecera">
        <h4>{{ $t("darDeBajaEtiqueta", { nombre: tag.nombre }) }}</h4>
      </div>

      <div class="modal-body baja-cuerpo">
        <!-- Lo que va a pasar, antes de pedir nada: la decision se toma sabiendo el
             precio. Sin materias primas ni hijas dentro no hay nada que decidir. -->
        <ul class="baja-consecuencias" v-if="materiasPrimas || hijas">
          <li v-if="materiasPrimas">
            {{ $tc("bajaConMateriasPrimas", materiasPrimas, { n: materiasPrimas }) }}
          </li>
          <li v-if="hijas">
            {{ $tc("bajaHijasSuben", hijas, { n: hijas, padre: nombreAbuelo }) }}
          </li>
        </ul>

        <p class="baja-sin" v-else>{{ $t("bajaSinConsecuencias") }}</p>

        <div class="baja-campo" v-if="materiasPrimas">
          <label for="bajaDestino">{{ $t("aQueEtiquetaVan") }}</label>

          <el-select
            id="bajaDestino"
            class="select-default baja-select"
            popper-class="baja-desplegable"
            v-model="idTagDestino"
            filterable
            :placeholder="$t('elegirEtiquetaDestino')"
            :no-match-text="$t('sinResultados')"
          >
            <el-option-group :label="$t('etiquetasDeMateriaPrima')">
              <el-option
                v-for="t in destinoVisible"
                :key="'d' + t.id"
                :value="t.id"
                :label="t.ruta"
              >
                <span class="op-ruta">{{ t.ruta }}</span>
                <span class="op-num">{{ t.materiasPrimas }}</span>
              </el-option>
            </el-option-group>

            <el-option-group v-if="destinoOculto.length" :label="$t('destinoOculto')">
              <el-option
                v-for="t in destinoOculto"
                :key="'o' + t.id"
                :value="t.id"
                :label="t.ruta"
              >
                <span class="op-ruta oculta">{{ t.ruta }}</span>
                <span class="op-num">{{ t.materiasPrimas }}</span>
              </el-option>
            </el-option-group>
          </el-select>

          <p class="baja-nota" v-if="destinoElegidoOculto">{{ $t("avisoDestinoOculto") }}</p>

          <!-- Dejarlas sin etiqueta es la salida de emergencia, no una opcion del
               desplegable: no es una etiqueta. Se marca a mano y se ve lo que implica. -->
          <label class="baja-chk" :class="{ puesto: sinEtiqueta }">
            <input type="checkbox" v-model="sinEtiqueta" />
            {{ $tc("bajaDejarSinEtiqueta", materiasPrimas, { n: materiasPrimas }) }}
          </label>
        </div>

        <p class="baja-error" v-if="error">{{ error }}</p>
      </div>

      <div class="modal-footer baja-pie">
        <button type="button" class="btn btn-claro" :disabled="ocupado" @click="cerrar()">
          {{ $t("cancelar") }}
        </button>
        <button
          type="button"
          class="btn btn-rojo"
          :disabled="ocupado || !puedeConfirmar"
          @click="confirmar()"
        >
          {{ $t("darDeBaja") }}
        </button>
      </div>
    </div>
  </modal>
</template>

<script>
import { Select, Option, OptionGroup } from "element-ui";

/**
 * Baja de una etiqueta, diciendo a donde va lo que tenia dentro.
 *
 * Antes esto era un `swal` que avisaba -"tiene 41 materias primas dentro, que se quedaran
 * apuntando a una etiqueta de baja"- y dejaba dar de baja igual. De ahi salen las 95
 * materias primas del 6730 atrapadas: la etiqueta existe, el subproducto la apunta, pero
 * la ficha ya no la ofrece, asi que desde el panel no habia forma de sacarlas.
 *
 * Con materias primas dentro, el boton no se activa hasta elegir destino. El API lo
 * vuelve a exigir por su cuenta, para que no dependa de esta pantalla.
 *
 * Igual que EtiquetaNuevaModal, este componente NO escribe: emite `confirmar` con
 * {idTag, idTagDestino} y la pantalla llama al endpoint.
 */
export default {
  name: "EtiquetaBajaModal",

  props: {
    nombreModal: { type: String, default: "modalBajaEtiqueta" },
    // Todas las etiquetas del negocio, tal como las devuelve getBusinessTags.
    etiquetas: { type: Array, default: () => [] },
    ocupado: { type: Boolean, default: false }
  },

  data() {
    return {
      tag: { id: 0, nombre: "", code: 0, parentCode: 0, tipo: "A", materiasPrimas: 0 },
      idTagDestino: null,
      sinEtiqueta: false,
      error: ""
    };
  },

  computed: {
    materiasPrimas() {
      return this.tag.materiasPrimas || 0;
    },

    /**
     * Las que cuelgan de ella y estan de alta. Mismo criterio que el API: mismo tipo
     * -el espacio de codes esta compartido entre familias- y sin contarse a si misma,
     * que en produccion pasa: hay 27 etiquetas que son su propio padre.
     */
    hijas() {
      let vm = this;

      return this.etiquetas.filter(function(t) {
        return (
          t.parentCode &&
          t.parentCode === vm.tag.code &&
          t.tipo === vm.tag.tipo &&
          !t.baja &&
          t.id !== vm.tag.id
        );
      }).length;
    },

    /**
     * A donde van a parar las hijas: el padre de la que se da de baja, o la raiz.
     */
    nombreAbuelo() {
      let vm = this;

      if (!this.tag.parentCode || this.tag.parentCode === this.tag.code) {
        return this.$t("ningunaSeranPrincipales");
      }

      let abuelo = this.etiquetas.find(function(t) {
        return t.code === vm.tag.parentCode && t.tipo === vm.tag.tipo;
      });

      return abuelo ? abuelo.nombre : this.$t("ningunaSeranPrincipales");
    },

    /**
     * De materia prima, de alta y nunca la que se esta dando de baja.
     */
    etiquetasDestino() {
      let vm = this;

      return this.etiquetas
        .filter(function(t) {
          return t.tipo === "A" && !t.baja && t.id !== vm.tag.id;
        })
        .sort(function(a, b) {
          return (a.ruta || "").localeCompare(b.ruta || "");
        });
    },

    destinoVisible() {
      return this.etiquetasDestino.filter(function(t) {
        return t.alcanzable;
      });
    },

    destinoOculto() {
      return this.etiquetasDestino.filter(function(t) {
        return !t.alcanzable;
      });
    },

    destinoElegidoOculto() {
      let vm = this;

      return this.destinoOculto.some(function(t) {
        return t.id === vm.idTagDestino;
      });
    },

    puedeConfirmar() {
      if (!this.materiasPrimas) {
        return true;
      }

      return this.sinEtiqueta || !!this.idTagDestino;
    }
  },

  watch: {
    // Son excluyentes: marcar "dejarlas sin etiqueta" descarta el destino elegido, y
    // elegir destino desmarca la casilla. Si no, no se sabe cual gana.
    sinEtiqueta(puesto) {
      if (puesto) {
        this.idTagDestino = null;
      }
    },

    idTagDestino(valor) {
      if (valor) {
        this.sinEtiqueta = false;
      }
    }
  },

  methods: {
    abrir(tag) {
      this.tag = tag;
      this.idTagDestino = null;
      this.sinEtiqueta = false;
      this.error = "";

      this.$modal.show(this.nombreModal);
    },

    cerrar() {
      this.$modal.hide(this.nombreModal);
    },

    mostrarError(mensaje) {
      this.error = mensaje || this.$t("errorInesperado");
    },

    confirmar() {
      if (!this.puedeConfirmar) {
        return;
      }

      this.error = "";

      this.$emit("confirmar", {
        idTag: this.tag.id,
        // -1 cuando no hay nada que recolocar, para que el API sepa distinguir
        // "no habia materias primas" de "las dejo sin etiqueta".
        idTagDestino: this.materiasPrimas ? (this.sinEtiqueta ? 0 : this.idTagDestino) : -1
      });
    }
  },

  components: {
    [Select.name]: Select,
    [Option.name]: Option,
    [OptionGroup.name]: OptionGroup
  }
};
</script>

<style scoped>
/* ---------------------------------------------------------------------------
   Baja de etiqueta

   Mismo lenguaje que el alta: cabecera clara, cuerpo blanco, pie gris. Lo que
   cambia es el color del boton de confirmar, rojo porque es destructivo, y que
   primero se ensena lo que va a pasar y solo despues se pide la decision.
   --------------------------------------------------------------------------- */
.baja-cabecera {
    padding: 16px 20px;
    background: #f7f9fb;
    border-bottom: 1px solid #e6e9ef;
}

.baja-cabecera h4 {
    margin: 0;
    font-size: 1.05em;
    font-weight: 600;
    color: #2c3e50;
}

.baja-cuerpo {
    padding: 18px 20px;
}

/* En ambar, como los avisos de la pantalla: son consecuencias, no errores. */
.baja-consecuencias {
    margin: 0 0 16px;
    padding: 10px 14px 10px 30px;
    background: rgba(214, 158, 46, 0.09);
    border: 1px solid rgba(214, 158, 46, 0.25);
    border-radius: 4px;
    font-size: 0.88em;
    color: #3d4a58;
}

.baja-consecuencias li + li {
    margin-top: 5px;
}

.baja-sin {
    margin: 0;
    font-size: 0.88em;
    color: #4a5765;
}

/* Esta no es una etiqueta de campo, es LA pregunta del modal: la decision que
   hay que tomar para poder seguir. En el gris de rotulo y en versalitas de 0.74em
   se leia como un adorno del desplegable. Va en el color del texto principal, sin
   mayusculas y al tamano de una frase. */
.baja-campo > label {
    display: block;
    font-size: 0.94em;
    color: #2c3e50;
    font-weight: 500;
    margin-bottom: 8px;
}

.baja-select {
    width: 100%;
}

.baja-nota {
    margin: 6px 0 0;
    font-size: 0.78em;
    color: #8a6420;
}

/* Mas discreta que la pregunta de arriba -es la alternativa, no lo que se viene a
   hacer- pero legible: decide el destino de las materias primas, asi que no puede
   quedarse en el gris de las notas al pie. */
.baja-chk {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin: 12px 0 0;
    font-size: 0.86em;
    font-weight: 400;
    color: #4a5765;
    cursor: pointer;
    user-select: none;
}

.baja-chk input {
    margin: 0;
    cursor: pointer;
}

.baja-chk:hover {
    color: #2c3e50;
}

.baja-chk.puesto {
    color: #2c3e50;
    font-weight: 500;
}

.baja-error {
    margin: 14px 0 0;
    padding: 9px 12px;
    border-radius: 4px;
    background: rgba(161, 58, 47, 0.1);
    border: 1px solid rgba(161, 58, 47, 0.22);
    color: #a13a2f;
    font-size: 0.85em;
}

.baja-pie {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding: 14px 20px;
    background: #f7f9fb;
    border-top: 1px solid #e6e9ef;
}

.btn-rojo {
    background: #a13a2f;
    border: 1px solid #a13a2f;
    color: #fff;
    border-radius: 3px;
    padding: 8px 16px;
    font-size: 0.88em;
    font-weight: 600;
}

.btn-rojo:hover:not(:disabled) {
    background: #862f26;
    border-color: #862f26;
    color: #fff;
}

.btn-rojo:disabled {
    opacity: 0.45;
    cursor: not-allowed;
}

.btn-claro {
    background: #fff;
    border: 1px solid #dde2e9;
    color: #4a5765;
    border-radius: 3px;
    padding: 8px 16px;
    font-size: 0.88em;
}

.btn-claro:hover:not(:disabled) {
    border-color: #009982;
    color: #009982;
}

.btn-claro:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}
</style>

<!-- Sin `scoped`: element-ui cuelga el desplegable del `body`. Acotado por
     `popper-class`, asi que no toca ningun otro `el-select`. -->
<style>
.baja-desplegable .el-select-dropdown__item {
    display: flex;
    align-items: center;
    gap: 12px;
}

.baja-desplegable .op-ruta {
    flex: 1 1 auto;
    overflow: hidden;
    text-overflow: ellipsis;
}

.baja-desplegable .op-ruta.oculta {
    color: #96690f;
}

.baja-desplegable .op-num {
    flex: 0 0 auto;
    min-width: 28px;
    text-align: right;
    font-size: 0.85em;
    font-variant-numeric: tabular-nums;
    color: #98a2ad;
}

.baja-desplegable .el-select-dropdown__item.selected .op-num,
.baja-desplegable .el-select-dropdown__item.hover .op-num {
    color: #4a5765;
}

.baja-desplegable .el-select-group__title {
    font-size: 0.72em;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-weight: 600;
    color: #8b96a3;
}
</style>



// WEBPACK FOOTER //
// src/pages/Widgets/Etiquetas/EtiquetaBajaModal.vue